import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import _ from 'lodash'
import * as Sentry from '@sentry/browser'
import { toast } from 'react-toastify'
import { Document, Page, pdfjs } from 'react-pdf'
import { saveAs } from 'file-saver'

import {
  Button,
  CircularProgress,
  Fab,
  Chip,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined'
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined'
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'

import { getConsultResponse, postFile, postGenerateCsv, postGeneratePdf, putUpdateRequest } from 'services/apiIrMain'
import {
  dateIsOlderThanYesterday,
  getFileNameExtension,
} from 'utils/functions'
import { FILE_TYPE } from 'utils/constants/types'

import AlertMessage from 'ui/components/AlertMessage'
import { Tabs } from 'ui/components'
import IrValues from './IrValues'
import IrReceiptValues from './IrReceiptValues'
import DcbeValues from './DcbeValues'
import DcbeReceiptValues from './DcbeReceiptValues'
import ListConsults from './ListConsults'
import Container from './styles'
import IrSummary from './IrSummary'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

const Home = () => {
  const userId = useSelector((state) => state.auth.sub)
  const apiKey = useSelector((state) => state.auth.api_key)

  const tokenDocument = useRef()
  const irInputFile = useRef()
  const dcbeInputFile = useRef()

  const [loading, setLoading] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [imgsBase64, setImgsBase64] = useState([])
  const [sendedFiles, setSendedFiles] = useState(false)
  const [showStillProcessingMessage, setShowStillProcessingMessage] = useState(false)
  const [processingIssue, setProcessingIssue] = useState(null)
  const [lastSendDate, setLastSendDate] = useState(null)
  const [activeUploadTab, setActiveUploadTab] = useState(0)
  const [showUploadPanel, setShowUploadPanel] = useState(true)
  const [showResultPanel, setShowResultPanel] = useState(false)
  const [showComparePanel, setShowComparePanel] = useState(false)
  const [isExportingPDF, setIsExportingPDF] = useState(false)
  const [consultName, setConsultName] = useState('')

  const [requestResponse, setRequestResponse] = useState(null)

  const uploadTabs = [{ label: 'IR' }, { label: 'DCBE' }]
  const activeUploadFileType = activeUploadTab === 0 ? FILE_TYPE.IR : FILE_TYPE.DCBE
  const activeUploadLabel = activeUploadTab === 0 ? 'IR' : 'DCBE'
  const filesInputDisabled = loading || sendedFiles || !_.isEmpty(selectedItem)
  const filesFromActiveTab = _.reduce(imgsBase64, (acc, fileData, fileIndex) => {
    if (fileData.fileType === activeUploadFileType) {
      acc.push({ fileData, fileIndex })
    }

    return acc
  }, [])

  // Count files by type
  const irFilesCount = _.filter(imgsBase64, { fileType: FILE_TYPE.IR }).length
  const dcbeFilesCount = _.filter(imgsBase64, { fileType: FILE_TYPE.DCBE }).length
  const pdfFilesFromMemory = _.reduce(imgsBase64, (acc, fileData, fileIndex) => {
    if (getFileNameExtension(fileData.name) === 'pdf') {
      acc.push({ fileData, fileIndex })
    }

    return acc
  }, [])
  const shouldShowCompareFab = showResultPanel
    && !showUploadPanel
    && _.isEmpty(selectedItem)
    && !_.isEmpty(pdfFilesFromMemory)
  const isSmallCompareScreen = useMediaQuery('(max-width: 1280px) and (max-height: 720px)')
  const reportAxiosError = (err, message = 'Ocorreu um erro inesperado') => {
    Sentry.captureException(err)

      const messageError = _.get(err, 'response.data.msg_errors.0.msg') || message
      toast.error(messageError)
  }

  const resetStates = (options = {}) => {
    const {
      resetToken = true,
      resetImgsBase64 = true,
      resetSelectedItem = true,
    } = options

    if (resetToken) tokenDocument.current = null
    if (resetImgsBase64) setImgsBase64([])
    if (resetSelectedItem) setSelectedItem(null)
    if (irInputFile.current) irInputFile.current.value = null
    if (dcbeInputFile.current) dcbeInputFile.current.value = null
    setLastSendDate(null)
    setLoading(false)
    setSendedFiles(false)
    setRequestResponse(null)
    setProcessingIssue(null)
    setConsultName('')
  }

  const handleResetAndShowUpload = () => {
    resetStates()
    setShowUploadPanel(true)
    setShowResultPanel(false)
    setShowComparePanel(false)
  }

  const handleFile = async (event, fileType) => {
    if (sendedFiles === true) {
      setImgsBase64([])
    }

    const { files } = event.target
    const newFiles = []

    _.each(files, file => {
      // TODO: check if file is image or pdf
      // TODO: validar tamanho dos arquivos
      const reader = new FileReader()

      reader.onloadend = () => {
        newFiles.push({
          name: file.name,
          base64: reader.result,
          fileType: fileType,
        })
        if (_.size(newFiles) === _.size(files)) {
          const filesUniq = _.differenceBy(newFiles, imgsBase64, 'base64')
          if (!_.isEmpty(filesUniq)) setImgsBase64(prevVal => ([ ...prevVal, ...filesUniq ]))
        }
      }

      reader.readAsDataURL(file)
    })
  }

  const handleDocumentPdfLoadSuccess = ({ numPages }, fileIndex) => {
    const newImgsBase64 = _.cloneDeep(imgsBase64)
    _.set(
      newImgsBase64,
      `${fileIndex}.pdfNumPages`,
      numPages,
    )

    setImgsBase64(newImgsBase64)
  }

  const handleDocumentPdfLoadError = (err) => {
    console.error(err.message)
    Sentry.captureException(err)
  }

  const handleFileDelete = (fileIndex) => {
    const newFiles = _.cloneDeep(imgsBase64)
    newFiles.splice(fileIndex, 1)
    setImgsBase64(newFiles)
  }

  const loadFilesResult = async (token, intervalId = null) => {
    const handleMsgInfos = (res) => {
      const msgInfos = _.get(res.data, 'msg_infos')
      if (_.isEmpty(msgInfos)) return false

      const allowedInfoMessages = ['Documento já validado', 'Limite menor que U$S 18 mil.']
      const benignMsgInfos = _.filter(msgInfos, (msgInfo) =>
        _.includes(allowedInfoMessages, msgInfo.msg),
      )
      const criticalMsgInfos = _.filter(
        msgInfos,
        (msgInfo) => !_.includes(allowedInfoMessages, msgInfo.msg),
      )

      if (!_.isEmpty(benignMsgInfos)) {
        _.each(benignMsgInfos, (msgInfo) => {
          toast.info(msgInfo.msg)
        })
      }

      if (!_.isEmpty(criticalMsgInfos)) {
        setProcessingIssue({
          title: 'Ocorreu um erro',
          content: 'Não foi possível processar os arquivos.',
        })
        setLoading(false)
      }
      return true
    }

    const handleMsgErrors = (err) => {
      const originalErrorMsg = _.get(err, 'response.data.msg_errors.0.msg')
      let errorMessageToShow = originalErrorMsg
      if (!originalErrorMsg) {
        errorMessageToShow = 'Erro ao obter dados'
      }
      else if (originalErrorMsg.includes('Ocorreu um erro ao processar a seção')) {
        errorMessageToShow = 'Ocorreu um erro ao processar o arquivo'
      }

      clearInterval(intervalId)

      setLoading(false)
      setSendedFiles(true)
      setShowUploadPanel(false)
      setShowResultPanel(true)
      setShowComparePanel(false)
      setRequestResponse(null)
      setSelectedItem(null)
      setProcessingIssue({
          title: 'Ocorreu um erro',
          content: errorMessageToShow,
        })
      }

    const handleSucessfulResult = (res) => {
      setSendedFiles(true)
      setRequestResponse(res.data)
      setShowUploadPanel(false)
      setShowResultPanel(true)
      setShowComparePanel(false)

      setLoading(false)
    }

    let res = null
    setLoading(true)
    setShowStillProcessingMessage(false)

    try {
      res = await getConsultResponse(
        apiKey,
        userId,
        token,
      )
    } catch (err) {
      Sentry.captureException(err)
      handleMsgErrors(err)
      return
    }

    if (!intervalId && res.status === 202) {
      setLoading(false)
      setShowStillProcessingMessage(true)
      return
    }

    if (res.status !== 200) return

    clearInterval(intervalId)

    if (handleMsgInfos(res)) return


    handleSucessfulResult(res)
  }

  const getFilesResult = (token) => {
    const intervalId = setInterval(async () => {
      await loadFilesResult(token, intervalId)
    }, 5000)
  }


  const handleExportCsv = async () => {
    let res = null

    if (_.isEmpty(requestResponse?.ir_response?.declaration)) {
      toast.error('Recurso indisponível. Arquivo de declaração não enviado.')
      return
    }

    try {
      res = await postGenerateCsv(
        apiKey,
        userId,
        tokenDocument.current,
      )
    } catch (err) {
      Sentry.captureException(err)

      const messageError = _.get(err, 'response.data.msg_errors.0.msg') || 'Erro exportar CSV'
      toast.error(messageError)
      return
    }

    const blobText = new Blob([res.data], { type: 'text/csv;charset=utf-8' })
    saveAs(blobText, 'exported.csv')
  }

  useEffect(() => {
    if (isExportingPDF) {
      window.print()
      setIsExportingPDF(false)
    }

  }, [isExportingPDF])

  const handleExportPdf = async () => {
    let res = null

    if (_.isEmpty(requestResponse?.ir_response?.declaration)) {
      toast.error('Recurso indisponível. Arquivo de declaração não enviado.')
      return
    }

    try {
      res = await postGeneratePdf(
        apiKey,
        userId,
        tokenDocument.current,
      )
    } catch (err) {
      Sentry.captureException(err)

      const messageError = _.get(err, 'response.data.msg_errors.0.msg') || 'Erro exportar PDF'
      toast.error(messageError)
      return
    }

    const blobText = new Blob([res.data], { type: 'application/pdf' })
    saveAs(blobText, 'exported.pdf')
  }

  const sendFile = async (fileContent, position, filesQuantity, token = null, consultationName = null) => {
    const res = await postFile(
      apiKey,
      userId,
      {
        token: token,
        name: fileContent.name, // name é o nome do arquivo.
        request_name: consultationName, // request_name é o nome da consulta inteira, conjunto de arquivos
        base64: fileContent.base64,
        fileType: fileContent.fileType,
        filePosition: position,
        totalRequests: filesQuantity,
      },
    )

    return res.data.token
  }

  const handleSubmit = async (files) => {
    if (_.isEmpty(files)) return

    setLoading(true)

    const filesTreated = _.map(files, (file) => {
      return {
        ...file,
        base64: file.base64.substr(file.base64.indexOf(',') + 1),
      }
    })

    const filesLength = filesTreated.length

    try {
      tokenDocument.current = await sendFile(filesTreated[0], 1, filesLength, null, consultName || null)
    } catch (err) {
      Sentry.captureException(err)
      setLoading(false)

      const messageError = _.get(err, 'response.data.msg_errors.0.msg') || 'Erro ao enviar arquivos'
      toast.error(messageError)
      return
    }

    try {
      const promises = _.map(filesTreated.slice(1), (fileTreated, i) => {
        return sendFile(fileTreated, i + 2, filesLength, tokenDocument.current, null, consultName || null)
      })
      await Promise.all(promises)
    } catch (err) {
      Sentry.captureException(err)
      setLoading(false)

      const messageError = _.get(err, 'response.data.msg_errors.0.msg') || 'Erro ao enviar arquivos'
      toast.error(messageError)
      return
    }

    setLastSendDate((new Date()).toString())
    getFilesResult(tokenDocument.current)
  }

  const handleIrDeclarationCheck = (event, itemId) => {
    putUpdateRequest(apiKey, userId, tokenDocument.current, {
      ir_assets: [{ id: itemId, checked: event.target.checked }],
    }).then((response) => {
      setRequestResponse(response.data)
    })
  }


  const handleDcbeDeclarationCheck = (event, fileIndex, declarationIndex) => {
    const newRequestResponse = _.cloneDeep(requestResponse)

    _.set(
      requestResponse.ir_response,
      `${fileIndex}.asset_details.${declarationIndex}.checked`,
      event.target.checked,
    )

    setRequestResponse(newRequestResponse)
  }

  const handleFinalizeOperation = async () => {
    try {
      await putUpdateRequest(
        apiKey,
        userId,
        tokenDocument.current,
        { conclude: true },
      )
    } catch (err) {
      reportAxiosError(err, 'Erro ao finalizar a operação')
    }

    resetStates()
    setShowUploadPanel(true)
    setShowResultPanel(false)
    setShowComparePanel(false)
    setLastSendDate((new Date()).toString())
  }

  const handleSelectItem = (item) => {
    resetStates()

    tokenDocument.current = item.token
    setSelectedItem(item)
    setShowUploadPanel(false)
    setShowResultPanel(true)
    setShowComparePanel(false)
    loadFilesResult(item.token)
  }

  const handleUploadClick = () => {
    if (filesInputDisabled) return

    const activeInputRef = activeUploadFileType === FILE_TYPE.IR ? irInputFile : dcbeInputFile
    if (activeInputRef.current) activeInputRef.current.click()
  }

  const toggleComparePanel = () => {
    setShowComparePanel(!showComparePanel)
  }

  const renderFileTypeIr = () => {
    const irResponse = requestResponse?.ir_response
    const usdData = {
      sale_quotation_usd: irResponse?.sale_quotation_usd || irResponse?.receipt?.sale_quotation_usd,
      quotation_datetime: irResponse?.quotation_datetime || irResponse?.receipt?.quotation_datetime,
    }

    return (
      <React.Fragment>

        <IrSummary usdData={usdData} receiptData={irResponse?.receipt} irDeclaration={irResponse?.declaration} settings={requestResponse?.setting} />

        <br />

        <IrReceiptValues receiptData={irResponse?.receipt} />

        <br />

        <IrValues
          irDeclaration={irResponse?.declaration}
          settings={requestResponse?.setting}
          onDeclarationCheck={handleIrDeclarationCheck}
          readOnly={!_.isEmpty(selectedItem)}
          isExportingPDF={isExportingPDF}
        />
      </React.Fragment>
    )
  }

  const renderFileTypeDcbe = () => {
    const dcbeResponse = requestResponse?.dcbe_response

    return (
      <React.Fragment>
        <DcbeReceiptValues
          filesData={dcbeResponse?.receipt}
        />

        <DcbeValues
          filesData={dcbeResponse}
          validTotal={dcbeResponse?.valid_total}
          onDeclarationCheck={handleDcbeDeclarationCheck}
          groupedValues={dcbeResponse?.groupedValues}
          hasSelectedItem={!_.isEmpty(selectedItem)}
        />
      </React.Fragment>
    )
  }

  const renderStillProcessingMessage = () => {
    if (!sendedFiles) return null

    if (processingIssue) {
      return (
        <AlertMessage
          iconType="error"
          severity="error"
          title={processingIssue.title}
          content={processingIssue.content}
        />
      )
    }

    if (!showStillProcessingMessage || _.isEmpty(selectedItem)) return null

    const isOld = dateIsOlderThanYesterday(selectedItem?.created_at)
    return (
      <AlertMessage
        action={<Button onClick={() => loadFilesResult(selectedItem?.token)}>Recarregar</Button>}
        iconType={isOld ? 'error' : 'loading'}
        severity={isOld ? 'error' : 'info'}
        title={isOld ? 'Ocorreu um erro' : 'Por favor, aguarde'}
        content={isOld
          ? 'Embora este OCR já seja antigo, parece que ele não foi processado corretamente.'
          : 'Este OCR ainda está sendo processado.'}
      />
    )
  }

  const renderResults = () => {


    return (
      <React.Fragment>
        {renderStillProcessingMessage()}
        { _.isEmpty(processingIssue) && (
        <React.Fragment>
          <h2 style={{ alignSelf: 'flex-start', marginBottom: 10, marginTop: 30 }}>
            Tipo: IR
          </h2>
          {renderFileTypeIr()}

          <h2 style={{ alignSelf: 'flex-start', marginBottom: 10, marginTop: 30 }}>
            Tipo: DCBE
          </h2>
          {renderFileTypeDcbe()}
        </React.Fragment>
)}
      </React.Fragment>
    )
  }
  return (
    <Container
      className={`
        ${showUploadPanel && !showResultPanel ? 'upload-only' : ''}
        ${!showUploadPanel && showResultPanel && !showComparePanel ? 'results-only' : ''}
        ${showComparePanel ? 'compare-mode' : ''}
      `}
    >
      <ListConsults onSelectItem={handleSelectItem} lastUpdatedDate={lastSendDate} hideButton={true} />

      {showUploadPanel && (
        <div className="files-container">
          <Paper className="files-upload-panel" elevation={0}>
            <div className="upload-panel-header">
              <div>
                <Typography className="upload-panel-title" variant="h6">
                  Upload de arquivos
                </Typography>
                <Typography className="upload-panel-subtitle" variant="body2">
                  Selecione os documentos de
                  {' '}
                  <strong>{activeUploadLabel}</strong>
                  {' '}
                  para análise.
                </Typography>
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {irFilesCount > 0 && (
                  <Chip
                    className="upload-count-chip"
                    icon={<DescriptionOutlinedIcon />}
                    label={`${irFilesCount} arquivo${irFilesCount === 1 ? '' : 's'} de IR`}
                    variant="outlined"
                  />
                )}
                {dcbeFilesCount > 0 && (
                  <Chip
                    className="upload-count-chip"
                    icon={<DescriptionOutlinedIcon />}
                    label={`${dcbeFilesCount} arquivo${dcbeFilesCount === 1 ? '' : 's'} de DCBE`}
                    variant="outlined"
                  />
                )}
              </div>
            </div>

            <Divider />

            <div className="upload-tabs-container">
              <Tabs tabs={uploadTabs} onChange={setActiveUploadTab} />
            </div>

            <div className="upload-actions-container">
              <input
                ref={ref => { irInputFile.current = ref }}
                accept="image/png,image/jpeg,image/jpg,application/pdf"
                disabled={filesInputDisabled}
                multiple
                onChange={(e) => handleFile(e, FILE_TYPE.IR)}
                type="file"
                className="input-file-hidden"
              />

              <input
                ref={ref => { dcbeInputFile.current = ref }}
                accept="image/png,image/jpeg,image/jpg,application/pdf"
                disabled={filesInputDisabled}
                multiple
                onChange={(e) => handleFile(e, FILE_TYPE.DCBE)}
                type="file"
                className="input-file-hidden"
              />

              <div className="upload-actions-main">
                <Button
                  className="upload-trigger"
                  color="primary"
                  disabled={filesInputDisabled}
                  onClick={handleUploadClick}
                  startIcon={<CloudUploadOutlinedIcon />}
                  variant="outlined"
                >
                  Escolher arquivos
                </Button>

                <Typography className="upload-actions-hint" variant="caption">
                  São aceitos arquivos PDF. Prefira os arquivos originais ao invés de cópias digitalizadas.
                </Typography>
              </div>
            </div>

            <div className="upload-submit-container">
              <TextField
                className="upload-name-input"
                disabled={loading || sendedFiles}
                label="Nome da consulta (opcional)"
                onChange={(event) => setConsultName(event.target.value)}
                placeholder="Nome da consulta (opcional)"
                size="small"
                value={consultName}
                variant="outlined"
              />

              <Button
                color="primary"
                disabled={loading || sendedFiles || _.isEmpty(imgsBase64)}
                onClick={() => handleSubmit(imgsBase64)}
                variant="contained"
              >
                Enviar
              </Button>
            </div>
          </Paper>

          <div className="files-files-container">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2rem', padding: '4rem 2rem', justifyContent: 'center' }}>
                <CircularProgress />
                <Typography variant="body1" style={{ textAlign: 'center' }}>
                  Processando arquivo(s). Por favor, aguarde.
                </Typography>
              </div>
            ) : (
              <React.Fragment>
                {_.map(filesFromActiveTab, ({ fileData, fileIndex }) => (
                  <div key={fileData.base64}>
                    <div className="img-btn-delete-container">
                      <Fab
                        size="small"
                        color="primary"
                        aria-label="add"
                        onClick={() => handleFileDelete(fileIndex)}
                      >
                        <DeleteOutlinedIcon />
                      </Fab>
                    </div>
                    <div className="file-preview-badge">
                      <DescriptionOutlinedIcon fontSize="small" />
                      <span>{fileData.name}</span>
                    </div>
                    {
                      getFileNameExtension(fileData.name) === 'pdf' ? (
                        <Document
                          file={fileData.base64}
                          onLoadSuccess={(data) => handleDocumentPdfLoadSuccess(data, fileIndex)}
                          onLoadError={handleDocumentPdfLoadError}
                        >
                          {_.map(new Array(fileData.pdfNumPages), (_val, indexPage) => (
                            <Page pageNumber={indexPage + 1} key={indexPage.toString()} className="PDF-Page" />
                          ))}
                        </Document>
                      ) : <img src={fileData.base64} alt="imagem" width="100%" />
                    }
                  </div>
                ))}
              </React.Fragment>
            )}
          </div>
        </div>
      )}

      {showComparePanel && !showUploadPanel && (
        <div className="files-container">
          <div className="files-files-container">
            <React.Fragment>
              {_.map(pdfFilesFromMemory, ({ fileData, fileIndex }) => (
                <div key={fileData.base64}>
                  <Document
                    file={fileData.base64}
                    onLoadSuccess={(data) => handleDocumentPdfLoadSuccess(data, fileIndex)}
                    onLoadError={handleDocumentPdfLoadError}
                  >
                    {_.map(new Array(fileData.pdfNumPages), (_val, indexPage) => (
                      <Page pageNumber={indexPage + 1} key={indexPage.toString()} className="PDF-Page" />
                    ))}
                  </Document>
                </div>
              ))}
            </React.Fragment>
          </div>
        </div>
      )}

      {showResultPanel && (
        <div className="content-container">
          <div className="content-btns-container">
            <Button
              disabled={!sendedFiles || !_.isEmpty(processingIssue)}
              onClick={() => handleExportCsv()}
              variant="contained"
            >
              Exportar para CSV
            </Button>

            <Button
              disabled={!sendedFiles || !_.isEmpty(processingIssue)}
              onClick={handleExportPdf}
              variant="contained"
            >
              Exportar para PDF
            </Button>

            {(!_.isEmpty(selectedItem) || !_.isEmpty(processingIssue)) ? (
              <Button
                color="primary"
                disabled={loading}
                onClick={handleResetAndShowUpload}
                variant="contained"
              >
                Limpar!
              </Button>
            ) : (
              <Button
                color="primary"
                disabled={!sendedFiles}
                onClick={() => handleFinalizeOperation()}
                variant="contained"
              >
                Finalizar Operação!
              </Button>
            )}
          </div>

          <div className="values-container">
            {
              loading
              ? (
                <React.Fragment>
                  <CircularProgress />
                </React.Fragment>
              )
             : renderResults()

            }
          </div>
        </div>
      )}

      {shouldShowCompareFab && (
        <Fab
          className="fab-show-upload"
          color="primary"
          variant={isSmallCompareScreen ? 'round' : 'extended'}
          onClick={toggleComparePanel}
        >
          {isSmallCompareScreen
            ? (showComparePanel ? <ChevronLeftIcon /> : <ChevronRightIcon />)
            : (showComparePanel ? '<<' : '>> Comparar')}
        </Fab>
      )}
    </Container>
  )
}

export default Home
